/**
 * Enhanced StoreSwitcher Component with Success Overlay
 * Dropdown component for switching between stores with clear feedback
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { formatPhoneNumber } from '../../utils/formatters';

const StoreSwitcher = ({ variant = 'desktop' }) => {
  const navigate = useNavigate();
  const {
    store: currentStore,
    availableStores,
    hasMultipleStores,
    selectStore,
    getStoreDisplayName,
    canProceedToShopping
  } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchingStoreId, setSwitchingStoreId] = useState(null);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [switchedStoreName, setSwitchedStoreName] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Auto-redirect after successful store switch
  useEffect(() => {
    if (showSuccessOverlay) {
      const timer = setTimeout(() => {
        setShowSuccessOverlay(false);
        // Redirect to homepage to refresh the store context
        navigate('/', { replace: true });
      }, 2500); // 2.5 second delay

      return () => clearTimeout(timer);
    }
  }, [showSuccessOverlay, navigate]);

  // Don't render if user can't proceed to shopping or has only one store
  if (!canProceedToShopping() || !hasMultipleStores()) {
    return null;
  }

  const handleStoreSwitch = async (store) => {
    if (store.id === currentStore?.id || isSwitching) return;

    setIsSwitching(true);
    setSwitchingStoreId(store.id);
    setSwitchedStoreName(store.name);

    try {
      await selectStore(store);
      setIsOpen(false);
      setIsSwitching(false);
      setSwitchingStoreId(null);

      // Show success overlay
      setShowSuccessOverlay(true);
    } catch (error) {
      console.error('Error switching stores:', error);
      alert('Failed to switch stores. Please try again.');
      setIsSwitching(false);
      setSwitchingStoreId(null);
    }
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isSwitching && !showSuccessOverlay) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <>
      <div className="dropdown" ref={dropdownRef}>
        <button
          className={`btn ${variant === 'mobile' ? 'btn-dark btn-sm' : 'btn-outline-light'} dropdown-toggle d-flex align-items-center rounded-0 ${
            (isSwitching || showSuccessOverlay) ? 'disabled' : ''
          }`}
          type="button"
          onClick={toggleDropdown}
          disabled={isSwitching || showSuccessOverlay}
          style={{ minWidth: variant === 'mobile' ? '140px' : '200px' }}
        >
          <i className="bi bi-shop me-2"></i>
          <span className="flex-grow-1 text-start text-truncate">
            {isSwitching ? 'Switching...' : getStoreDisplayName()}
          </span>
          {isSwitching && (
            <div className="spinner-border spinner-border-sm ms-2" role="status">
              <span className="visually-hidden">Switching...</span>
            </div>
          )}
        </button>

        <div className={`dropdown-menu dropdown-menu-end shadow-lg ${isOpen ? 'show' : ''}`} style={{ minWidth: '320px' }}>
          <div className="dropdown-header d-flex align-items-center justify-content-between">
            <span className="fw-bold">Switch Store</span>
            <span className="badge bg-primary">{availableStores.length} available</span>
          </div>
          
          <div className="dropdown-divider"></div>

          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {availableStores.map((store) => {
              const isCurrentStore = store.id === currentStore?.id;
              const isLoadingThis = switchingStoreId === store.id;
              
              return (
                <button
                  key={store.id}
                  type="button"
                  className={`dropdown-item px-3 py-2 ${isCurrentStore ? 'active' : ''} ${isLoadingThis ? 'disabled' : ''}`}
                  onClick={() => !isCurrentStore && !isLoadingThis && handleStoreSwitch(store)}
                  disabled={isCurrentStore || isLoadingThis}
                  style={{ 
                    cursor: isCurrentStore ? 'default' : (isLoadingThis ? 'wait' : 'pointer'),
                    opacity: isLoadingThis ? 0.7 : 1
                  }}
                >
                  <div className="d-flex align-items-start">
                    <div className="me-3 mt-1">
                      {isCurrentStore ? (
                        <i className="bi bi-check-circle-fill text-success"></i>
                      ) : isLoadingThis ? (
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      ) : (
                        <i className="bi bi-shop text-muted"></i>
                      )}
                    </div>
                    
                    <div className="flex-grow-1">
                      <div className="fw-semibold text-dark">
                        {store.name}
                        {isCurrentStore && (
                          <span className="badge bg-success ms-2 small">Current</span>
                        )}
                      </div>
                      
                      <div className="small text-muted mt-1">
                        <div className="d-flex align-items-center mb-1">
                          <i className="bi bi-geo-alt me-1" style={{ fontSize: '0.75rem' }}></i>
                          <span>{store.address}, {store.city}</span>
                        </div>
                        
                        {store.phone && (
                          <div className="d-flex align-items-center">
                            <i className="bi bi-telephone me-1" style={{ fontSize: '0.75rem' }}></i>
                            <span>{formatPhoneNumber(store.phone)}</span>
                          </div>
                        )}
                      </div>
                      
                      {isLoadingThis && (
                        <div className="small text-primary mt-1">
                          Switching store...
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

        </div>
      </div>

      {/* Success Overlay - Same pattern as StoreSelector */}
      {showSuccessOverlay && createPortal(
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
             style={{
               backgroundColor: 'rgba(0, 0, 0, 0.8)',
               zIndex: 9999,
               backdropFilter: 'blur(4px)'
             }}>
          <div className="card border-0 shadow-lg" style={{ maxWidth: '420px' }}>
            <div className="card-body text-center p-5">
              <div className="mb-4">
                <div className="rounded-circle bg-success bg-opacity-10 d-inline-flex align-items-center justify-content-center mx-auto"
                     style={{ width: '80px', height: '80px' }}>
                  <i className="bi bi-arrow-repeat text-success" style={{ fontSize: '2.5rem' }}></i>
                </div>
              </div>

              <h3 className="text-success fw-bold mb-3">Store Switched!</h3>
              <p className="text-muted mb-2">
                You've successfully switched to <strong>{switchedStoreName}</strong>
              </p>
              <p className="small text-muted mb-4">
                Your previous cart has been cleared for the new store.
              </p>

              <div className="d-flex align-items-center justify-content-center text-muted">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <small>Refreshing homepage...</small>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default StoreSwitcher;